import env from 'env';

export interface MapboxFeature {
  id: string;
  type: 'Feature';
  place_name: string;
  address?: string;
  properties: {
    accuracy?: string;
    address?: string;
    category?: string;
    maki?: string;
    wikidata?: string;
    short_code?: string;
  };
  text: string;
  place_type: string[];
  relevance: number;
  center: [number, number]; // [longitude, latitude]
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  context?: Array<{
    id: string;
    text: string;
    short_code?: string;
    wikidata?: string;
  }>;
}

export interface MapboxGeocodingResponse {
  type: 'FeatureCollection';
  query: string[];
  features: MapboxFeature[];
  attribution: string;
}

export interface GeocodingOptions {
  /**
   * Limit results to one or more countries (ISO 3166 alpha 2 country codes)
   * @example ['AU', 'NZ'] for Australia and New Zealand
   */
  country?: string[];

  /**
   * Bias results towards a geographic location
   * @example [144.9631, -37.8136] for Melbourne
   */
  proximity?: [number, number];

  /**
   * Limit results to a bounding box
   * @example [minLng, minLat, maxLng, maxLat]
   */
  bbox?: [number, number, number, number];

  /**
   * Filter results to include only specified place types
   * @example ['address', 'poi']
   */
  types?: string[];

  /**
   * Maximum number of results to return (1-10)
   */
  limit?: number;

  /**
   * Language for returned text (ISO 639-1 language code)
   */
  language?: string;
}

class MapboxGeocodingService {
  private baseUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
  private accessToken = env.MAPBOX_TOKEN;

  /**
   * Search for addresses and places using a search string
   */
  async searchAddresses(
    searchString: string,
    options: GeocodingOptions = {},
  ): Promise<MapboxGeocodingResponse> {
    if (!this.accessToken) {
      throw new Error('Mapbox access token is not configured');
    }

    if (!searchString.trim()) {
      throw new Error('Search string cannot be empty');
    }

    const {
      country = ['AU'], // Default to Australia based on your project
      proximity,
      bbox,
      types,
      limit = 5,
      language = 'en',
    } = options;

    // Build query parameters
    const params = new URLSearchParams({
      access_token: this.accessToken,
      limit: limit.toString(),
      language,
    });

    if (country.length > 0) {
      params.append('country', country.join(','));
    }

    if (proximity) {
      params.append('proximity', proximity.join(','));
    }

    if (bbox) {
      params.append('bbox', bbox.join(','));
    }

    if (types && types.length > 0) {
      params.append('types', types.join(','));
    }

    // Encode the search string for URL safety
    const encodedQuery = encodeURIComponent(searchString.trim());
    const url = `${this.baseUrl}/${encodedQuery}.json?${params.toString()}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Mapbox API error: ${response.status} ${response.statusText}`,
        );
      }

      const data: MapboxGeocodingResponse = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch geocoding results: ${error.message}`);
      }
      throw new Error(
        'Unknown error occurred while fetching geocoding results',
      );
    }
  }

  /**
   * Search for addresses only (excludes POIs, landmarks, etc.)
   */
  async searchAddressesOnly(
    searchString: string,
    options: Omit<GeocodingOptions, 'types'> = {},
  ): Promise<MapboxGeocodingResponse> {
    return this.searchAddresses(searchString, {
      ...options,
      types: ['address'],
    });
  }

  /**
   * Search for places (POIs, landmarks, etc.)
   */
  async searchPlaces(
    searchString: string,
    options: Omit<GeocodingOptions, 'types'> = {},
  ): Promise<MapboxGeocodingResponse> {
    return this.searchAddresses(searchString, {
      ...options,
      types: ['poi', 'place'],
    });
  }

  /**
   * Reverse geocoding - get address from coordinates
   */
  async reverseGeocode(
    longitude: number,
    latitude: number,
    options: Omit<GeocodingOptions, 'proximity' | 'bbox'> = {},
  ): Promise<MapboxGeocodingResponse> {
    if (!this.accessToken) {
      throw new Error('Mapbox access token is not configured');
    }

    const { country = ['AU'], types, limit = 1, language = 'en' } = options;

    const params = new URLSearchParams({
      access_token: this.accessToken,
      limit: limit.toString(),
      language,
    });

    if (country.length > 0) {
      params.append('country', country.join(','));
    }

    if (types && types.length > 0) {
      params.append('types', types.join(','));
    }

    const url = `${
      this.baseUrl
    }/${longitude},${latitude}.json?${params.toString()}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Mapbox API error: ${response.status} ${response.statusText}`,
        );
      }

      const data: MapboxGeocodingResponse = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(
          `Failed to fetch reverse geocoding results: ${error.message}`,
        );
      }
      throw new Error(
        'Unknown error occurred while fetching reverse geocoding results',
      );
    }
  }
}

export default new MapboxGeocodingService();
